// Ubicación en el repo: src/pages/Login.jsx
import { useEffect, useState } from 'react';
import { login, verifyMfaChallenge, enrollTotp, confirmTotpEnrollment, logout, requestPasswordReset } from '../lib/auth';
import { useSession } from '../lib/SessionContext';
import PasswordField from '../components/PasswordField';

export default function Login() {
  const { status, session, mfaSatisfied, needsMfaEnrollment } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Rate limiting de login (5 intentos fallidos en 15 min, migración 0023):
  // en vez de "contraseña incorrecta" otra vez, se ofrece pedir un correo
  // de acceso nuevo — no hace falta esperar a que un Admin desbloquee nada.
  const [lockedOut, setLockedOut] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Enrolamiento: datos del QR (se piden una sola vez al entrar a este
  // paso) y el código que el usuario escribe para confirmarlo.
  const [enrollData, setEnrollData] = useState(null); // { factorId, qrCode, secret }
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollLoading, setEnrollLoading] = useState(false);

  // Si ya pasamos el paso de password (hay sesión) pero falta verificar un
  // 2FA ya existente, se muestra el paso de verificación. Si en cambio el
  // rol exige 2FA y todavía no hay ningún factor, se muestra el paso de
  // enrolamiento — son pasos distintos, mutuamente excluyentes.
  const showMfaStep = status === 'ready' && !!session && !mfaSatisfied;
  const showEnrollStep = status === 'ready' && !!session && mfaSatisfied && needsMfaEnrollment;

  useEffect(() => {
    if (!showEnrollStep || enrollData) return;
    setError('');
    enrollTotp()
      .then(setEnrollData)
      .catch((err) => setError(err.message || 'No se pudo iniciar el enrolamiento de 2FA.'));
  }, [showEnrollStep, enrollData]);

  async function handleEnrollSubmit(e) {
    e.preventDefault();
    setError('');
    setEnrollLoading(true);
    try {
      await confirmTotpEnrollment(enrollData.factorId, enrollCode.trim());
      // La verificación exitosa sube el aal y dispara un evento de auth;
      // SessionContext recalcula solo y la redirección ocurre sola.
    } catch (err) {
      setError(err.message || 'Código incorrecto.');
    } finally {
      setEnrollLoading(false);
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      // Si no hace falta 2FA, el cambio de sesión dispara solo la
      // redirección (ver SessionContext + RedirectIfAuthed en App.jsx).
      // Si hace falta, showMfaStep pasa a true y se muestra el paso 2.
    } catch (err) {
      if (err.code === 'ACCOUNT_LOCKED') {
        setLockedOut(true);
      } else {
        setError(err.message || 'No se pudo iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestReset() {
    setError('');
    setResetLoading(true);
    try {
      await requestPasswordReset(identifier.trim());
      setResetSent(true);
    } catch (err) {
      setError(err.message || 'No se pudo enviar el correo.');
    } finally {
      setResetLoading(false);
    }
  }

  async function handleMfaSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfaChallenge(mfaCode.trim());
      // La verificación exitosa sube el aal de la sesión actual; el
      // próximo evento de auth lo recalcula y la redirección ocurre sola.
    } catch (err) {
      setError(err.message || 'Código incorrecto.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <img src="/agendadock-logo.png" alt="AgendaDock" className="auth-card__logo" />
        <h1>Agenda Comunal</h1>
        <p className="auth-subtitle">by GlobalVision</p>

        {showEnrollStep ? (
          <div>
            <p className="auth-subtitle">
              Tu rol exige verificación en dos pasos. Escanea este código con Google Authenticator, Authy, u
              otra app similar, y después escribe el código de 6 dígitos que te muestre.
            </p>
            {enrollData ? (
              <form onSubmit={handleEnrollSubmit}>
                <div className="mfa-qr">
                  <img src={enrollData.qrCode} alt="Código QR para 2FA" width={180} height={180} />
                </div>
                <p className="field-hint">
                  ¿No puedes escanearlo? Escribe este código manualmente en tu app:
                  <br />
                  <code className="mfa-secret">{enrollData.secret}</code>
                </p>
                <label className="field">
                  <span>Código (6 dígitos)</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value)}
                    autoFocus
                    required
                  />
                </label>
                {error && <p className="auth-error">{error}</p>}
                <button type="submit" disabled={enrollLoading}>
                  {enrollLoading ? 'Activando…' : 'Activar'}
                </button>
                <button type="button" className="link-button" onClick={() => logout()}>
                  Cerrar sesión
                </button>
              </form>
            ) : (
              !error && <p className="auth-subtitle">Generando el código…</p>
            )}
          </div>
        ) : lockedOut ? (
          <div>
            <p className="auth-error">
              Demasiados intentos fallidos. Por seguridad, esta cuenta quedó bloqueada temporalmente.
            </p>
            {resetSent ? (
              <p className="field-hint">
                Si la cuenta existe, se envió un correo con un link para definir una contraseña nueva y entrar de
                inmediato.
              </p>
            ) : (
              <>
                <p className="field-hint" style={{ marginBottom: 16 }}>
                  Puedes pedir que te enviemos un correo para definir una contraseña nueva y entrar sin esperar.
                </p>
                <button
                  type="button"
                  className="button-primary auth-wide-btn"
                  onClick={handleRequestReset}
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Enviando…' : 'Enviarme un correo de acceso'}
                </button>
              </>
            )}
            {error && <p className="auth-error">{error}</p>}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setLockedOut(false);
                setResetSent(false);
                setError('');
              }}
            >
              Volver
            </button>
          </div>
        ) : !showMfaStep ? (
          <form onSubmit={handlePasswordSubmit}>
            <label className="field">
              <span>RUT o correo</span>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </label>
            <label className="field">
              <span>Contraseña</span>
              <PasswordField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleMfaSubmit}>
            <p className="auth-subtitle">Ingresa el código de tu app autenticadora.</p>
            <label className="field">
              <span>Código (6 dígitos)</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoFocus
                required
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Verificando…' : 'Verificar'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
